import { useEffect, useRef } from 'react';
import type { DrawView, SessionSnapshot } from '../game/session';
import { sideName } from '../game/sideName';

interface Props {
  snapshot: SessionSnapshot;
  draw: DrawView & { asking: string };
  onAnswer: (accept: boolean) => void;
}

/**
 * A draw offered to the side a person here plays (D-46), asked over the board: Accept ends the
 * game drawn, Decline plays on. Decline takes the focus and Escape declines, as Cancel does in a
 * confirmation: a stray Return must not end a game.
 */
export function DrawOfferDialog({ snapshot, draw, onAnswer }: Props) {
  const declineRef = useRef<HTMLButtonElement>(null);
  // The game's snapshots re-render this often: the focus is set once, where the offer opens.
  const answer = useRef(onAnswer);
  answer.current = onAnswer;
  const from = sideName(snapshot, draw.from);
  const to = sideName(snapshot, draw.asking);

  useEffect(() => {
    declineRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.preventDefault();
      answer.current(false);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  return (
    <div className="confirm" role="presentation">
      <div
        className="confirm-card"
        role="alertdialog"
        aria-modal="true"
        aria-label={`${from} offers a draw`}
        data-testid="draw-offer"
      >
        <h2 className="menu-heading">{from} offers a draw</h2>
        <p className="hint">
          {to}: accept, and the game ends drawn. A rated draw counts as half a win for each side.
        </p>
        <div className="confirm-actions">
          <button ref={declineRef} onClick={() => onAnswer(false)} data-testid="draw-decline">
            Decline
          </button>
          <button className="primary" onClick={() => onAnswer(true)} data-testid="draw-accept">
            Accept the draw
          </button>
        </div>
      </div>
    </div>
  );
}
